import { useCallback, useState } from "react";
import { IPartnerProfileChartProps } from "../../interfaces/PartnerView.interfaces";
import { CustomCardWithCharts } from "../../SharedComponents/CustomCardWithCharts";
import { groupBy } from "../../Utils/js-common";
import { CUSTOMER_VIEW_STYLES } from "../CustomerView/CustomerView.styles";
import { ChartDataOption } from "../../SharedComponents/Interfaces/CustomCardWithCharts.interfaces";

const PartnerProfileChart = (props: IPartnerProfileChartProps) => {
  // Properties
  const [metricFilters, setMetricFilters] = useState<{ [key: string]: any }>({});

  // Events
  const handleResetFilters = useCallback(() => {
    setMetricFilters({});
    props.onSelectedChartFilter({
      filterKey: "",
      value: ""
    });
  }, [props]);
  /**
   * onClick action for each datapoint in the chart
   */

  const handleDataPointClick = useCallback(
    (option: ChartDataOption<any>) => {
      const { field, title, value } = option;
      let filterKey = field;
      let filterValue = value;

      switch (value) {
        case "CSP":
          filterKey = "isCSP";
          filterValue = true;
          break;
        case "CPOR":
          filterKey = "isCPOR";
          filterValue = true;
          break;
        case "PAL":
          filterKey = "isPAL";
          filterValue = true;
          break;
        default:
          // keep field and value as-is
          break;
      }

      props.onSelectedChartFilter({
        filterKey,
        value: filterValue
      });

      setMetricFilters((prev) => {
        const currentValue = prev[title];
        let newValue;

        if (currentValue) {
          // Check if the value is already in the comma-separated list
          const currentValues = currentValue.split(",").map((v: string) => v.trim());

          if (currentValues.includes(value)) {
            // If value exists, remove it (toggle off)
            const filteredValues = currentValues.filter((v: string) => v !== value);
            newValue = filteredValues.length > 0 ? filteredValues.join(", ") : undefined;
          } else {
            // If value doesn't exist, add it
            newValue = `${currentValue}, ${value}`;
          }
        } else {
          // First value for this title
          newValue = value;
        }

        // If newValue is undefined, remove the key entirely
        if (newValue === undefined) {
          const newState = { ...prev };
          delete newState[title];
          return newState;
        }

        return { ...prev, [title]: newValue };
      });
    },
    [props]
  );

  // Events
  const getGroupedCountData = (key: string, data: any[]) => {
    const filteredData = data.filter((item) => item[key]);
    const groupedData = groupBy(filteredData, key);

    return Object.keys(groupedData)?.map((item) => ({
      name: item,
      value: groupedData[item].length
    }));
  };

  // Events
  const getPartnerTypeCountData = (data: any[]) => {
    // Count only if the respective field is true
    const cspCount = data.filter((item) => item["isCSP"] === true).length;
    const cporCount = data.filter((item) => item["isCPOR"] === true).length;
    const palCount = data.filter((item) => item["isPAL"] === true).length;

    return [
      { name: "CSP", value: cspCount },
      { name: "CPOR", value: cporCount },
      { name: "PAL", value: palCount }
    ];
  };

  const partnerTypeDistribution = getPartnerTypeCountData(props.filteredData);
  const countryDistribution = getGroupedCountData("partnerCountry", props.filteredData);

  return (
    <div className={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CLASS_NAMES.customCardContainer}>
      <CustomCardWithCharts
        enableUrlParams
        loading={props.loading}
        metricFilters={metricFilters}
        onDataPointClick={handleDataPointClick}
        onResetFilters={handleResetFilters}
        options={{
          chartOptions: [
            {
              id: 1,
              field: "workloadNames",
              key: "partner-view-partner-profile-chart-by-workloadName",
              title: "MPNs By Partner Association",
              tooltipDescription: "Distribution of partners by their MPN associations (CSP, CPOR, PAL).",
              type: "verticalbar",
              kustoLink: props.associationTypeKustoLink,
              groupedData: partnerTypeDistribution
            },
            {
              id: 2,
              field: "partnerCountry",
              key: "partner-view-partner-profile-chart-by-partnerCountry",
              title: "Partner's Country Distribution",
              tooltipDescription: "Distribution of partners by their country.",
              type: "donut",
              kustoLink: props.partnerCountryKustoLink,
              groupedData: countryDistribution
            }
          ]
        }}
      />
    </div>
  );
};

export default PartnerProfileChart;
